import { useCallback, useMemo } from 'react'

import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { invalidateBlockApprovalRequestQueries } from '~/hooks/bidding/queryKeys'
import { useApproveBiddingBlockApprovalRequests } from '~/pages/SpotBidding/hooks/useApproveBiddingBlockApprovalRequests'
import { useApproverIdentity } from '~/pages/SpotBidding/hooks/useApproverIdentity'
import type { BlockApprovalDecision } from '~/pages/SpotBidding/hooks/useBlockApprovalDecision'
import { useBlockBidLimits } from '~/pages/SpotBidding/hooks/useBlockBidLimits'
import type { BulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'
import { useBulkApprovalFlow } from '~/pages/SpotBidding/hooks/useBulkApprovalFlow'
import {
  blockWriteBlockedReasonLabel,
  translateBlockWriteError,
} from '~/pages/SpotBidding/models/blockBidWrite'
import type { BulkFailureMessages } from '~/pages/SpotBidding/models/bulkApproval'
import { normalizeBulkResult } from '~/pages/SpotBidding/models/bulkApproval'
import { buildBlockBulkCases } from '~/pages/SpotBidding/models/bulkApprovalCases'
import type { VolumeCountableBid } from '~/pages/SpotBidding/models/spotBidValidation'

type Params = {
  /** PENDING かつ明細 Get 解決済みの group 群 (`useBlockApprovalDecision` に渡すものと同じ)。 */
  pendingGroups: BiddingBlockApprovalRequest[]
  /** 上限数量(コマ計) の再検証母集合 (`useBlockApprovalDecision.volumeTotalRows` と同じ)。 */
  volumeTotalRows: readonly VolumeCountableBid[]
  isSpotVolumeUnresolved: boolean
  /** 単件の承認と共通の可否 policy (締切・確定済みワークフロー・明細未解決)。 */
  decision: Pick<
    BlockApprovalDecision,
    'decisionDisabledReason' | 'decisionBlockedReasonAt'
  >
  targetDate: Date
}

/**
 * ブロック入札タブのまとめて承認 (AC-BSP-118)。 グループ (LINK / LOOP はペアで 1 件) を案件に
 * 写し、 単件の承認確認と同じ入札制限の事前チェック・承認者の識別を案件ごとに当てる。 手順は
 * `useBulkApprovalFlow` が持つ。
 */
export const useBlockBulkApproval = ({
  pendingGroups,
  volumeTotalRows,
  isSpotVolumeUnresolved,
  decision,
  targetDate,
}: Params): BulkApprovalFlow => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { mutateAsync } = useApproveBiddingBlockApprovalRequests()
  const approverIdentity = useApproverIdentity()
  // 単件の承認確認と同じく、 入札制限は取得中も空 lookup で返るため、 取得中・取得失敗の間は
  // 再検証が成立しないものとして fail-closed にし、 開く時に取り直す。
  const {
    limits,
    isFetching: isLimitsFetching,
    isError: isLimitsError,
    refetch: refetchLimits,
  } = useBlockBidLimits()

  // 入口の補足と、 全件が承認者でない時に入口を出さない判断に使う。
  const notApproverCount = useMemo(
    () => pendingGroups.filter((r) => approverIdentity(r).isNotApprover).length,
    [approverIdentity, pendingGroups],
  )

  const buildCases = useCallback(
    (pending: readonly BiddingBlockApprovalRequest[]) =>
      buildBlockBulkCases({
        pendingGroups: pending,
        ctx: {
          limits,
          volumeTotalRows,
          isLimitsUnavailable: isLimitsFetching || isLimitsError,
          isSpotVolumeUnresolved,
        },
        identityOf: approverIdentity,
        t,
      }),
    [
      approverIdentity,
      isLimitsError,
      isLimitsFetching,
      isSpotVolumeUnresolved,
      limits,
      t,
      volumeTotalRows,
    ],
  )

  const failureMessages = useMemo<BulkFailureMessages>(
    () => ({
      updatedByOther: t('page:biddingBlock.serverError.aborted'),
      // 取消・削除は AC-BSP-073 の基準文言 (3 画面共通)。 ブロック入札単件の notFound は
      // 「削除された可能性」 だけなので使わない。
      canceledOrDeleted: t('page:biddingSpot.serverError.notFound'),
      gateClosure: t('page:biddingBlock.approvalAction.decisionDeadlinePassed'),
      other: t('page:biddingBlock.serverError.unknown'),
      violationFallback: t(
        'page:biddingBlock.serverError.reason.bidLimitViolation',
      ),
    }),
    [t],
  )

  return useBulkApprovalFlow({
    pending: pendingGroups,
    buildCases,
    notApproverCount,
    send: async (targets) => {
      const response = await mutateAsync({ targets })
      return response.results.map((r) =>
        normalizeBulkResult(r, (reason) =>
          blockWriteBlockedReasonLabel(reason, t),
        ),
      )
    },
    invalidate: () => invalidateBlockApprovalRequestQueries(queryClient),
    decisionDisabledReason: decision.decisionDisabledReason,
    decisionBlockedReasonAt: decision.decisionBlockedReasonAt,
    targetDate,
    failureMessages,
    translateError: (err) => translateBlockWriteError(err, t),
    onOpen: () => {
      void refetchLimits()
    },
  })
}
